import { useState, useEffect, useCallback } from "react";
import productsService from "../services/productsService";
import { CACHE_CONFIG } from "./useProductsCache";

const useHomeFeaturedProducts = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const [cacheSource, setCacheSource] = useState("none");

  // Función para guardar productos destacados en sessionStorage
  const saveHomeProductsToStorage = useCallback((products) => {
    try {
      if (!products || products.length === 0) return;

      const cacheData = {
        products,
        timestamp: Date.now(),
        version: CACHE_CONFIG.VERSION,
        count: products.length,
        type: "featured",
      };

      const metadata = {
        lastUpdate: Date.now(),
        version: CACHE_CONFIG.VERSION,
        productCount: products.length,
        source: "api",
        type: "home-featured",
      };

      sessionStorage.setItem(
        CACHE_CONFIG.HOME_STORAGE_KEY,
        JSON.stringify(cacheData)
      );
      sessionStorage.setItem(
        CACHE_CONFIG.HOME_METADATA_KEY,
        JSON.stringify(metadata)
      );

      console.log("🏠 Featured products saved to sessionStorage:", {
        count: products.length,
        size: `${(JSON.stringify(cacheData).length / 1024).toFixed(2)}KB`,
      });
    } catch (error) {
      console.warn(
        "⚠️ Failed to save featured products to sessionStorage:",
        error
      );
    }
  }, []);

  // Función para cargar productos destacados desde sessionStorage
  const loadHomeProductsFromStorage = useCallback(() => {
    try {
      return null; // No usar caché por ahora, siempre cargar desde API

      const cachedData = sessionStorage.getItem(CACHE_CONFIG.HOME_STORAGE_KEY);
      const metadata = sessionStorage.getItem(CACHE_CONFIG.HOME_METADATA_KEY);

      if (!cachedData || !metadata) {
        console.log("🏠 No featured products cache found");
        return null;
      }

      const parsedData = JSON.parse(cachedData);
      const parsedMetadata = JSON.parse(metadata);

      // Validar versión del caché
      if (parsedData.version !== CACHE_CONFIG.VERSION) {
        console.log("🔄 Featured cache version mismatch, clearing");
        clearHomeProductsStorage();
        return null;
      }

      // Validar TTL específico para productos destacados (más corto)
      const now = Date.now();
      const cacheAge = now - parsedData.timestamp;

      if (cacheAge > CACHE_CONFIG.HOME_TTL) {
        console.log("⏰ Featured cache expired:", {
          age: `${Math.round(cacheAge / 1000 / 60)}min`,
          ttl: `${CACHE_CONFIG.HOME_TTL / 1000 / 60}min`,
        });
        clearHomeProductsStorage();
        return null;
      }

      console.log("✅ Featured products loaded from cache:", {
        count: parsedData.products.length,
        age: `${Math.round(cacheAge / 1000 / 60)}min`,
      });

      return {
        products: parsedData.products,
        timestamp: parsedData.timestamp,
        metadata: parsedMetadata,
      };
    } catch (err) {
      console.warn("⚠️ Failed to load featured products from cache:", err);
      clearHomeProductsStorage();
      return null;
    }
  }, []);

  // Función para limpiar caché de productos destacados
  const clearHomeProductsStorage = useCallback(() => {
    try {
      sessionStorage.removeItem(CACHE_CONFIG.HOME_STORAGE_KEY);
      sessionStorage.removeItem(CACHE_CONFIG.HOME_METADATA_KEY);
      console.log("🗑️ Featured products cache cleared");
    } catch (err) {
      console.warn("⚠️ Failed to clear featured products cache:", err);
    }
  }, []);

  // Función para cargar productos destacados desde la API
  const fetchFeaturedProductsFromAPI = useCallback(async () => {
    console.log("🌟 Fetching featured products from API...");

    try {
      const response = await productsService.getProducts({
        page: 1,
        limit: CACHE_CONFIG.HOME_FEATURED_SIZE,
        featured: true, // Solo productos destacados
        area: 1,
      });

      if (response && response.products) {
        console.log("✅ Featured products API fetch completed:", {
          products: response.products.length,
          total: response.pagination?.total || 0,
        });

        return response.products;
      } else {
        throw new Error("No featured products received from API");
      }
    } catch (error) {
      console.error("❌ Error fetching featured products:", error);
      throw error;
    }
  }, []);

  // Cargar productos destacados
  const loadFeaturedProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setIsEmpty(false);

      // Intentar cargar desde caché primero
      const cachedData = loadHomeProductsFromStorage();

      if (cachedData && cachedData.products?.length > 0) {
        setFeaturedProducts(cachedData.products);
        setCacheSource("sessionStorage");
        setLoading(false);
        console.log("🚀 Featured products loaded from cache");
        return;
      }

      // Si no hay caché válido, cargar desde API
      setCacheSource("api");
      const freshProducts = await fetchFeaturedProductsFromAPI();

      // La API respondió correctamente
      setFeaturedProducts(freshProducts);

      if (freshProducts.length === 0) {
        // API respondió bien pero no hay productos destacados
        setIsEmpty(true);
        console.log(
          "📭 API responded successfully but no featured products available"
        );
      } else {
        saveHomeProductsToStorage(freshProducts);
        console.log("✅ Featured products loaded from API and cached");
      }
    } catch (err) {
      console.error("❌ Error loading featured products:", err);
      setError(err.message);
      setCacheSource("none");
    } finally {
      setLoading(false);
    }
  }, [
    loadHomeProductsFromStorage,
    fetchFeaturedProductsFromAPI,
    saveHomeProductsToStorage,
  ]);

  // Refrescar caché de productos destacados
  const refreshFeaturedCache = useCallback(async () => {
    console.log("🔄 Refreshing featured products cache...");
    clearHomeProductsStorage();
    setCacheSource("none");
    await loadFeaturedProducts();
  }, [clearHomeProductsStorage, loadFeaturedProducts]);

  // Cargar productos destacados al montar
  useEffect(() => {
    loadFeaturedProducts();
  }, [loadFeaturedProducts]);

  return {
    featuredProducts,
    loading,
    error,
    isEmpty,
    cacheSource,
    refreshFeaturedCache,
    clearFeaturedCache: clearHomeProductsStorage,
    // Stats para debugging
    featuredStats: {
      source: cacheSource,
      productCount: featuredProducts.length,
      hasCache: !!sessionStorage.getItem(CACHE_CONFIG.HOME_STORAGE_KEY),
    },
  };
};

export default useHomeFeaturedProducts;
